import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { Avatar, Badge, Box, Chip, Tooltip, Typography, useTheme } from "@mui/material";
import ExtensionOutlined from "@mui/icons-material/ExtensionOutlined";
import { getEquipmentType } from "../equipmentTypes";
import { getAccessibleTextColor } from "../nodeColorPalette";
import styles from "./styles";

// Cor base (hex do tema) para uma disponibilidade — aceita tanto o status agregado ("up"/"down")
// quanto os booleanos crus de SNMP/PING, normalizados nos mesmos dois estados
const ESTADOS_UP = new Set([true, "up"]);
const ESTADOS_DOWN = new Set([false, "down"]);
const corDisponibilidade = (valor, theme) =>
  ESTADOS_UP.has(valor)
    ? theme.palette.colors?.green?.medium
    : ESTADOS_DOWN.has(valor)
      ? theme.palette.colors?.red?.medium
      : theme.palette.colors?.gray?.medium;

// Rótulo curto de disponibilidade (true/false/null) para os chips do tooltip de status
const rotuloDisponibilidade = (valor) =>
  valor === true ? "Up" : valor === false ? "Down" : "N/D";

// Nó customizado do diagrama: ícone do tipo, nome (ou host) e observações do equipamento
function EquipmentNode({ data, selected = false, isConnectable = false }) {
  const theme = useTheme();
  const nodeStyles = styles(theme);

  // Resolve o tipo do equipamento (fixo ou personalizado) na lista mesclada injetada pelo canvas pai;
  // se o tipo não for encontrado (ex: tipo personalizado excluído), cai no ícone genérico abaixo
  const tipo = getEquipmentType(data?.tipo, data?._equipmentTypes);
  const Icone = tipo?.icon;
  const isCustomIcon = !!tipo?.isCustom;

  // Normaliza observacao para decidir se tooltip deve ser renderizado
  const observacoes = data?.observacoes?.trim?.() || "";

  // Nome exibido: preenchido pelo usuário ou, por padrão, o tipo do equipamento
  const nomeExibido = data?.nome || tipo?.label || "Sem nome";

  // Primeira linha não vazia de observações, exibida truncada como segunda linha
  // do card; o Tooltip abaixo continua mostrando o texto completo de observações
  const primeiraLinhaObservacoes =
    data?.observacoes
      ?.split?.(/\r?\n/)
      ?.map((linha) => linha.trim())
      ?.find((linha) => !!linha) || "";

  // Cor de agrupamento (calculada e injetada pelo canvas pai em data._colorSwatch — ver
  // nodesForCanvas em diagramaEquipamentos/index.js); ausente = comportamento padrao, sem cor
  const colorSwatch = data?._colorSwatch;

  // Status (SNMP/PING) do host vinculado, injetado pelo canvas/preview pai em data._hostStatus
  // (useHostStatus/hostStatusStore) e nome resolvido em data._hostLabel — nenhum dos dois é
  // persistido, só existem para renderização. Sem host vinculado, nenhum pontinho aparece.
  // Regra de negócio: o PING manda — down joga o status geral pra baixo mesmo com SNMP up/null.
  const hostVinculado = !!data?.hostId;
  const hostStatus = data?._hostStatus;
  const corStatusDot = hostVinculado ? corDisponibilidade(hostStatus?.status, theme) : null;

  // Cor de fundo + texto (contraste validado contra o próprio fundo, nunca fixo) dos chips de
  // SNMP/PING exibidos dentro do tooltip do pontinho
  const chipSx = (valor) => {
    const fundo = corDisponibilidade(valor, theme);
    return { backgroundColor: fundo, color: getAccessibleTextColor(fundo, theme) };
  };

  // Conteúdo do ícone extraído pra ser reaproveitado com ou sem o Badge de status (evita duplicar
  // o ternário de ícone fixo/personalizado/genérico nos dois ramos)
  const iconBox = (
    <Box sx={nodeStyles.nodeIconBox}>
      {isCustomIcon ? (
        <Avatar src={tipo?.iconUrl} variant="square" sx={nodeStyles.nodeIconCustom}>
          <ExtensionOutlined sx={nodeStyles.nodeIcon} />
        </Avatar>
      ) : Icone ? (
        <Icone sx={nodeStyles.nodeIcon} />
      ) : (
        <ExtensionOutlined sx={nodeStyles.nodeIcon} />
      )}
    </Box>
  );

  const interfacesVinculadas = data?.interfaces || [];
  const statusTooltip = hostVinculado ? (
    <Box>
      <Typography sx={nodeStyles.nodeTooltipTitle}>Status do host</Typography>
      <Box sx={nodeStyles.statusTooltipContent}>
        <Typography sx={nodeStyles.statusTooltipLine}>
          Host: {data?._hostLabel || data?.hostId}
        </Typography>
        {!!interfacesVinculadas.length && (
          <Typography sx={nodeStyles.statusTooltipLine}>
            Interfaces: {interfacesVinculadas.join(", ")}
          </Typography>
        )}
        <Box sx={nodeStyles.statusTooltipChips}>
          <Chip
            size="small"
            label={`SNMP: ${rotuloDisponibilidade(hostStatus?.snmp)}`}
            sx={chipSx(hostStatus?.snmp)}
          />
          <Chip
            size="small"
            label={`PING: ${rotuloDisponibilidade(hostStatus?.ping)}`}
            sx={chipSx(hostStatus?.ping)}
          />
        </Box>
      </Box>
    </Box>
  ) : null;

  // Mantem cartao unico para preservar handles e eventos do React Flow
  const nodeCard = (
    <Box
      sx={{
        ...nodeStyles.nodeCard,
        ...(colorSwatch
          ? {
              border: `2px solid ${colorSwatch.border}`,
              backgroundColor: colorSwatch.tint,
            }
          : {}),
        ...(selected ? nodeStyles.nodeCardSelected : {}),
        ...(selected && !colorSwatch ? nodeStyles.nodeCardSelectedSemCor : {}),
      }}
    >
      {/* Handles nos quatro lados: com connectionMode loose qualquer lado pode ser origem ou destino */}
      <Handle
        id="top"
        type="source"
        position={Position.Top}
        isConnectable={isConnectable}
        style={nodeStyles.handle}
      />
      <Handle
        id="right"
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        style={nodeStyles.handle}
      />
      <Handle
        id="bottom"
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        style={nodeStyles.handle}
      />
      <Handle
        id="left"
        type="source"
        position={Position.Left}
        isConnectable={isConnectable}
        style={nodeStyles.handle}
      />
      {/* Badge de status do host vinculado (SNMP/PING) sobre o ícone — some quando não há host
          atrelado. Ancorado no ícone (não no card inteiro) pra não competir com a borda de
          agrupamento/anel de seleção, que já ocupam o contorno externo do card */}
      {hostVinculado ? (
        <Tooltip title={statusTooltip} placement="top" arrow>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            variant="dot"
            sx={{
              "& .MuiBadge-badge": {
                ...nodeStyles.hostStatusBadgeDot,
                backgroundColor: corStatusDot,
              },
            }}
          >
            {iconBox}
          </Badge>
        </Tooltip>
      ) : (
        iconBox
      )}
      <Box sx={nodeStyles.nodeTextBox}>
        <Typography sx={nodeStyles.nodeName} title={nomeExibido}>
          {nomeExibido}
        </Typography>
        {!!primeiraLinhaObservacoes && (
          <Typography sx={nodeStyles.nodeSecondLine}>{primeiraLinhaObservacoes}</Typography>
        )}
      </Box>
    </Box>
  );

  return observacoes ? (
    <Tooltip
      title={
        <Box>
          <Typography sx={nodeStyles.nodeTooltipTitle}>Observações</Typography>
          <Box sx={nodeStyles.nodeTooltipContent}>{observacoes}</Box>
        </Box>
      }
      placement="top"
      arrow
      slotProps={{ tooltip: { sx: nodeStyles.nodeTooltip } }}
    >
      {nodeCard}
    </Tooltip>
  ) : (
    nodeCard
  );
}

// Registro dos tipos de nó do React Flow, declarado fora do componente para evitar recriação a cada render
export const nodeTypes = { equipamento: EquipmentNode };

export default memo(EquipmentNode);
